import { z } from 'zod';
import { BIOME_IDS } from './biomes.js';
import { BOSS_MOB_IDS } from './boss-mobs.js';
import { MobIdSchema } from './mobs.js';
export { BOSS_MOB_IDS, BOSS_RESPAWN_MS } from './boss-mobs.js';
/**
 * What a zone boss does besides biting (ADR 0024). Data, not code: a closed
 * vocabulary the server resolves through `rules/boss.ts`, so a new boss is a
 * row here, a body and a lair, never a new branch in the room.
 *
 * - An ability is a shape marked on the ground before it lands (ADR 0029).
 *   The boss stops for `windupMs` — the time to step out — then every
 *   player still inside takes a share of their greatest health, `multiplier`
 *   × `BOSS_SHAPE_SHARE_PER_MULTIPLIER`, whatever their armour (ADR 0088).
 *   Each boss fights with its own shapes:
 *   - `slam`: a square of `radius` on the tile its target stands on;
 *   - `nova`: a square of `radius` all round the boss;
 *   - `ring`: the tiles between `inner` and `radius` of the boss, the ring
 *     round it — safe against it, the opposite of a nova;
 *   - `line`: a charge or a breath, `length` tiles from the boss toward its
 *     target, `radius` tiles either side of its axis, stopped by walls;
 *   - `cross`: the four arms through the boss, `length` long, `radius`
 *     either side;
 *   - `rain`: `count` squares of `radius`, one on each player within
 *     `spread` of the boss and the rest at random within it.
 * - Two thresholds a life: at half health it calls its kin (`summon`), at a
 *   quarter it strikes faster (`enrage`).
 */
export const BOSS_ABILITY_KINDS = ['slam', 'nova', 'ring', 'line', 'cross', 'rain'];
export const BossAbilitySchema = z
    .object({
    kind: z.enum(BOSS_ABILITY_KINDS),
    /** French, for the chat line and the tooltip of the boss bar. */
    name: z.string().min(1),
    /**
     * Chebyshev radius in tiles: 1 is the tile and its eight neighbours. For a
     * line or a cross, how many tiles either side of its axis; for a rain, the
     * radius of each drop.
     */
    radius: z.number().int().min(0).max(4),
    /** A ring's hole: the tiles this close to the boss are spared. */
    inner: z.number().int().min(0).max(4).optional(),
    /** A line's or a cross's reach, in tiles from the boss. */
    length: z.number().int().min(1).max(10).optional(),
    /** A rain's drops, and how far from the boss they may fall. */
    count: z.number().int().min(1).max(10).optional(),
    spread: z.number().int().min(1).max(8).optional(),
    /** How long the disc stays on the ground before it lands. */
    windupMs: z.number().int().min(600).max(3000),
    /** Time between two uses, counted from the landing. */
    everyMs: z.number().int().min(3000),
    /** How hard it lands: × `BOSS_SHAPE_SHARE_PER_MULTIPLIER` of the target's greatest health (ADR 0088). */
    multiplier: z.number().positive().max(6),
})
    .refine((a) => (a.kind !== 'ring' || (a.inner !== undefined && a.inner < a.radius)) &&
    ((a.kind !== 'line' && a.kind !== 'cross') || a.length !== undefined) &&
    (a.kind !== 'rain' || (a.count !== undefined && a.spread !== undefined)), 'a ring needs its inner radius, a line or a cross its length, a rain its count and spread');
export const BossDefinitionSchema = z.object({
    id: z.enum(BOSS_MOB_IDS),
    biome: z.enum(BIOME_IDS),
    /** The lair's name on the maps and in the announcements. */
    lair: z.string().min(1),
    abilities: z.array(BossAbilitySchema).min(1),
    /** At this share of its health, once a life, it calls `count` of its kin around it. */
    summon: z.object({
        at: z.number().gt(0).lt(1),
        mob: MobIdSchema,
        count: z.number().int().min(1).max(4),
    }),
    /** At this share of its health, once a life, its attack interval is multiplied by `haste`. */
    enrage: z.object({ at: z.number().gt(0).lt(1), haste: z.number().gt(0).lt(1) }),
});
/** The kin come at half health; the rage at a quarter. */
const SUMMON_AT = 0.5;
const ENRAGE = { at: 0.25, haste: 0.7 };
const DEFINITIONS = {
    // The first boss: a scythe on its target, and crows that come down on
    // whoever stands near — easy to read, the lesson of every later fight.
    'great-scarecrow': {
        id: 'great-scarecrow',
        biome: 'meadow',
        lair: 'Champ Maudit',
        abilities: [
            {
                kind: 'slam',
                name: 'Coup de faux',
                radius: 1,
                windupMs: 1600,
                everyMs: 9000,
                multiplier: 2.5,
            },
            {
                kind: 'rain',
                name: 'Nuée de corbeaux',
                radius: 1,
                count: 4,
                spread: 5,
                windupMs: 1800,
                everyMs: 15000,
                multiplier: 1.5,
            },
        ],
        summon: { at: SUMMON_AT, mob: 'rat', count: 3 },
        enrage: ENRAGE,
    },
    // A hunter: it leaps down a lane at its prey (step aside), and howls all
    // round itself (step away).
    werewolf: {
        id: 'werewolf',
        biome: 'forest',
        lair: 'Clairière de la Lune',
        abilities: [
            {
                kind: 'line',
                name: 'Bond',
                radius: 1,
                length: 7,
                windupMs: 1400,
                everyMs: 8000,
                multiplier: 2.5,
            },
            {
                kind: 'nova',
                name: 'Hurlement',
                radius: 2,
                windupMs: 1600,
                everyMs: 14000,
                multiplier: 1.5,
            },
        ],
        summon: { at: SUMMON_AT, mob: 'wolf', count: 3 },
        enrage: ENRAGE,
    },
    // A brute: its club splits the ground in a cross (stand between the arms),
    // and the mud it stirs rolls out in a ring (hug it).
    'bog-troll': {
        id: 'bog-troll',
        biome: 'swamp',
        lair: 'Bourbier du Troll',
        abilities: [
            {
                kind: 'cross',
                name: 'Fracas de massue',
                radius: 1,
                length: 5,
                windupMs: 1600,
                everyMs: 10000,
                multiplier: 2.5,
            },
            {
                kind: 'ring',
                name: 'Onde de boue',
                radius: 3,
                inner: 1,
                windupMs: 1700,
                everyMs: 13000,
                multiplier: 2,
            },
        ],
        summon: { at: SUMMON_AT, mob: 'toad', count: 2 },
        enrage: ENRAGE,
    },
    // A priest of the sun: a thin, long ray at its target, and a storm of sand
    // round it that only its own feet are spared.
    'golden-jackal': {
        id: 'golden-jackal',
        biome: 'desert',
        lair: 'Temple du Chacal',
        abilities: [
            {
                kind: 'line',
                name: 'Rayon du soleil',
                radius: 0,
                length: 9,
                windupMs: 1300,
                everyMs: 7000,
                multiplier: 2.5,
            },
            {
                kind: 'ring',
                name: 'Tempête de sable',
                radius: 4,
                inner: 1,
                windupMs: 1600,
                everyMs: 13000,
                multiplier: 2,
            },
        ],
        summon: { at: SUMMON_AT, mob: 'mummy', count: 2 },
        enrage: ENRAGE,
    },
    // The last of them uses three: spikes of ice in a cross, hail over the
    // players, and the frost wave round it.
    'frost-giant': {
        id: 'frost-giant',
        biome: 'snow',
        lair: 'Trône de Givre',
        abilities: [
            {
                kind: 'cross',
                name: 'Pics de glace',
                radius: 0,
                length: 7,
                windupMs: 1400,
                everyMs: 8000,
                multiplier: 2.5,
            },
            {
                kind: 'rain',
                name: 'Grêle',
                radius: 1,
                count: 6,
                spread: 6,
                windupMs: 1600,
                everyMs: 12000,
                multiplier: 2,
            },
            {
                kind: 'nova',
                name: 'Onde glaciale',
                radius: 2,
                windupMs: 1600,
                everyMs: 16000,
                multiplier: 3,
            },
        ],
        summon: { at: SUMMON_AT, mob: 'polar-bear', count: 2 },
        enrage: ENRAGE,
    },
};
/** Parsed once at module load: a malformed boss fails the server at boot. */
export const BOSSES = Object.freeze(Object.fromEntries(BOSS_MOB_IDS.map((id) => [id, BossDefinitionSchema.parse(DEFINITIONS[id])])));
export function isBossId(mobId) {
    return BOSS_MOB_IDS.includes(mobId);
}
/** The boss definition of a mob id, if that mob is a zone boss. */
export function bossOf(mobId) {
    return isBossId(mobId) ? BOSSES[mobId] : undefined;
}
/** The boss of each biome. */
export function bossOfBiome(biome) {
    const found = BOSS_MOB_IDS.map((id) => BOSSES[id]).find((boss) => boss.biome === biome);
    if (found === undefined)
        throw new Error(`Biome ${biome} has no boss`);
    return found;
}
//# sourceMappingURL=bosses.js.map